// refreshMaterializedView — non-buffer maintenance action for materialized
// views (DSE2 §3.D3). Unlike every other VqeDdlEdit op, this does NOT mutate
// the Monaco buffer; it dispatches a REFRESH statement directly against the
// active connection.
//
// Per-engine surface:
//   - postgres: REFRESH MATERIALIZED VIEW [CONCURRENTLY] <name> [WITH NO DATA]
//   - oracle:   BEGIN DBMS_MVIEW.REFRESH('<name>', '<method>'); END;
//               method ∈ {'C' (COMPLETE), 'F' (FAST), '?' (FORCE)}
//   - mssql / mysql / sqlite: unsupported (no materialized views).
//
// PRD: Ralph/VqeDdlEdit/PRD.md § Slice D8.
import type { QualifiedName, SqlEngine } from '../../../sql-grammar/model';
import { synthesizeRefreshMaterializedView } from '../../../sql-resolver/ddl/apply/dialect.view';
import { executeQueryV2 } from '../../../database-objects/services/queryExecutor';

/** Oracle DBMS_MVIEW.REFRESH method (mapped to one-character codes). */
export type OracleRefreshMethod = 'complete' | 'fast' | 'force';

export interface RefreshMaterializedViewOptions {
  /** postgres-only: emit `REFRESH MATERIALIZED VIEW CONCURRENTLY …`. */
  readonly pgsqlConcurrently?: boolean;
  /** postgres-only: emit `WITH NO DATA` to truncate the matview to empty. */
  readonly pgsqlWithNoData?: boolean;
  /** oracle-only: refresh method passed to DBMS_MVIEW.REFRESH. */
  readonly oracleMethod?: OracleRefreshMethod;
}

export function isRefreshMaterializedViewSupported(engine: SqlEngine): boolean {
  return engine === 'postgres' || engine === 'oracle';
}

export function getRefreshMaterializedViewUnsupportedTooltip(engine: SqlEngine): string {
  if (engine === 'mssql') {
    return (
      'SQL Server has no materialized views. The closest equivalent is an '
      + 'indexed view (CREATE VIEW … WITH SCHEMABINDING + a unique clustered index).'
    );
  }
  if (engine === 'mysql') {
    return (
      'MySQL has no materialized views — there is no portable workaround. '
      + 'Refresh a precomputed table via INSERT … SELECT instead.'
    );
  }
  return 'SQLite has no materialized views. Use CREATE TABLE AS SELECT for a snapshot.';
}

/** Build the engine-correct refresh statement. Throws when the engine does
 *  not support refresh. */
export function buildRefreshMaterializedViewSql(
  engine: SqlEngine,
  name: QualifiedName,
  options: RefreshMaterializedViewOptions = {},
): string {
  if (!isRefreshMaterializedViewSupported(engine)) {
    throw new Error(
      `refreshMaterializedView: ${engine} does not support materialized-view refresh`,
    );
  }
  if (engine === 'postgres') {
    if (options.oracleMethod !== undefined) {
      throw new Error(
        `refreshMaterializedView: postgres does not accept 'oracleMethod' — `
          + `use 'pgsqlConcurrently' / 'pgsqlWithNoData' instead`,
      );
    }
    const withData = options.pgsqlWithNoData === true ? false : undefined;
    return synthesizeRefreshMaterializedView(
      'postgres',
      name,
      options.pgsqlConcurrently === true,
      withData,
    );
  }
  // oracle
  if (options.pgsqlConcurrently || options.pgsqlWithNoData) {
    throw new Error(
      `refreshMaterializedView: oracle does not accept postgres options `
        + `('pgsqlConcurrently' / 'pgsqlWithNoData') — use 'oracleMethod' instead`,
    );
  }
  const method = oracleMethodCode(options.oracleMethod ?? 'complete');
  const fullName = oracleQualifiedName(name);
  return `BEGIN DBMS_MVIEW.REFRESH('${escapeSingleQuotes(fullName)}', '${method}'); END;`;
}

function oracleMethodCode(method: OracleRefreshMethod): 'C' | 'F' | '?' {
  if (method === 'complete') return 'C';
  if (method === 'fast') return 'F';
  return '?';
}

function oracleQualifiedName(name: QualifiedName): string {
  if (name.raw && name.raw.length > 0) return name.raw;
  return name.schema ? `${name.schema}.${name.name}` : name.name;
}

function escapeSingleQuotes(input: string): string {
  return input.replace(/'/g, "''");
}

export interface ExecuteRefreshArgs {
  readonly engine: SqlEngine;
  readonly connectionId: string;
  readonly database?: string | null;
  readonly name: QualifiedName;
  readonly options?: RefreshMaterializedViewOptions;
  /** Test seam — overrides the global `executeQueryV2` (lets unit tests
   *  capture the dispatched request without mocking electronAPI). */
  readonly executor?: typeof executeQueryV2;
}

export interface ExecuteRefreshResult {
  readonly success: boolean;
  readonly sql: string;
  readonly error?: string;
}

/** Dispatch the refresh against the active connection. Returns a structured
 *  result so the consumer (popover) can surface a toast on success/failure. */
export async function executeRefreshMaterializedView(
  args: ExecuteRefreshArgs,
): Promise<ExecuteRefreshResult> {
  const sql = buildRefreshMaterializedViewSql(args.engine, args.name, args.options);
  const exec = args.executor ?? executeQueryV2;
  const result = await exec({
    connectionId: args.connectionId,
    sql,
    database: args.database ?? undefined,
    executionId: `vqe-ddl-refresh-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
  });
  if (result.success) return { success: true, sql };
  return { success: false, sql, error: result.error?.message ?? 'Refresh failed' };
}
